import { useEffect, useRef, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidatesServices, { CandidatesResponse } from '@/services/candidates';
import teamsServices, {
  CreateATeamParams,
  CreateTeamResponse,
  EditTeamParams,
} from '@/services/teams';
import Modal from '@/trio-ui/Modal';
import { FetchApiIds } from '@/types';
import { Team } from '@/types/api';
import NewTeamFormModalComponent, {
  FormData,
  NewTeamFormModalComponentProps,
} from './NewTeamFormModal.component';

type NewTeamFormModalProps = Pick<
  NewTeamFormModalComponentProps,
  'isOpen' | 'onClose' | 'editing'
> & {
  onCreateNewTeam?: (team: CreateTeamResponse) => void;
  onEditTeam?: (team: Team) => void;
  onRemoveTeam?: (teamId: string) => void;
};

const NewTeamFormModal = ({
  isOpen,
  onClose,
  onCreateNewTeam,
  onEditTeam,
  editing,
  onRemoveTeam,
}: NewTeamFormModalProps): JSX.Element => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [teamMembersSearch, setTeamMembersSearch] = useState<string>('');
  const [managerSearch, setManagerSearch] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<string>();
  const [lockDrawer, setLockDrawer] = useState<boolean>(false);
  const toast = useToastContext();

  useEffect(() => {
    const onPageClick = (event: any): void => {
      if (!isOpen) return;

      const eventPath = event.composedPath() ?? [];

      const doesElementContainId = (element: any): boolean => {
        return element === elementRef.current;
      };

      const isClickingInsideForm = eventPath.some(doesElementContainId);

      if (!confirmDelete && !lockDrawer && !isClickingInsideForm) {
        onClose?.();
      }
    };

    window.addEventListener('mousedown', onPageClick);

    return () => {
      window.removeEventListener('mousedown', onPageClick);
    };
  }, [elementRef, onClose, isOpen, lockDrawer, confirmDelete]);

  const teamMembersSearchResponse = useApiFetch<CandidatesResponse>(
    [FetchApiIds.CANDIDATES_FILTERED, teamMembersSearch],
    async () => {
      const [firstName, ...rest] = teamMembersSearch.split(' ');

      return candidatesServices.getFilteredCandidates({
        take: 10,
        page: 1,
        column: 'firstName',
        order: 'ASC',
        filters: JSON.stringify([
          {
            field: 'firstName',
            condition: 'stringContain',
            value: firstName,
          },
          ...(rest.length > 0
            ? [
                {
                  field: 'lastName',
                  condition: 'stringContain',
                  value: rest.join(' '),
                },
              ]
            : []),
        ]),
      });
    },
    { enabled: teamMembersSearch.length >= 2 }
  );

  const managerSearchResponse = useApiFetch<CandidatesResponse>(
    [FetchApiIds.CANDIDATES_FILTERED, managerSearch],
    async () => {
      const [firstName, ...rest] = managerSearch.split(' ');

      return candidatesServices.getFilteredCandidates({
        take: 10,
        page: 1,
        column: 'firstName',
        order: 'ASC',
        filters: JSON.stringify([
          {
            field: 'firstName',
            condition: 'stringContain',
            value: firstName,
          },
          ...(rest.length > 0
            ? [
                {
                  field: 'lastName',
                  condition: 'stringContain',
                  value: rest.join(' '),
                },
              ]
            : []),
        ]),
      });
    },
    { enabled: managerSearch.length >= 2 }
  );

  const createTeamMutation = useApiSubmit<CreateTeamResponse, CreateATeamParams>(
    teamsServices.createATeam
  );

  const editTeamMutation = useApiSubmit<Team, EditTeamParams>(teamsServices.editTeam);

  const deleteTeamMutation = useApiSubmit<unknown, string>(teamsServices.deleteTeam);

  const handleSubmit = async (data: FormData): Promise<void> => {
    try {
      if (editing) {
        const editedTeam = await editTeamMutation.mutateAsync({
          id: editing.id,
          area: data.area,
          category: data.category,
          managerId: data.managerId,
          personIdList: data.memberIds,
          name: data.name,
          file: data.image,
        });
        toast.setMessage('Team edited successfully.');
        toast.setSeverity('success');
        toast.showToast();
        onEditTeam?.(editedTeam);
      } else {
        const newTeam = await createTeamMutation.mutateAsync({
          area: data.area,
          category: data.category,
          managerId: data.managerId,
          personIdList: data.memberIds,
          name: data.name,
          file: data.image,
        });
        toast.setMessage('Team created successfully.');
        toast.setSeverity('success');
        toast.showToast();
        onCreateNewTeam?.(newTeam);
      }

      onClose?.();
    } catch (error: any) {
      toast.setMessage(
        JSON.parse(error?.request?.response).message || 'There was an error creating the team.'
      );
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const handleDeleteConfirmClick = async (teamId: string): Promise<void> => {
    try {
      await deleteTeamMutation.mutateAsync(teamId);
      toast.setMessage('Team removed successfully.');
      toast.setSeverity('success');
      toast.showToast();
      onRemoveTeam?.(teamId);
      onClose?.();
    } catch (error: any) {
      const pgCode = error?.response?.data?.code ?? error?.code;
      const detail = error?.response?.data?.details ?? error?.details ?? '';
      let msg = 'Something went wrong while removing the team. Please try again.';
      if (pgCode === '23503') {
        if (detail.includes('fx_vendors')) {
          msg = 'Can\'t delete this team — it has contractors or vendors linked to it. Move them to another team first.';
        } else if (detail.includes('team_members')) {
          msg = 'Can\'t delete this team — it still has members. Remove them first.';
        } else {
          msg = 'Can\'t delete this team — other data is still linked to it. Move or remove linked items first.';
        }
      }
      toast.setMessage(msg);
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      setConfirmDelete(undefined);
    }
  };

  const handleDeleteClick = (teamId: string): void => {
    setConfirmDelete(teamId);
  };

  useEffect(() => {
    if (isOpen) {
      setTeamMembersSearch('');
      setManagerSearch('');
    }
  }, [isOpen, setTeamMembersSearch, setManagerSearch]);

  return (
    <>
      <NewTeamFormModalComponent
        isOpen={isOpen}
        onClose={onClose}
        ref={elementRef}
        teamMemberSearch={teamMembersSearch}
        onTeamMembersSearch={setTeamMembersSearch}
        teamMembersFound={teamMembersSearchResponse?.data?.data}
        managerSearch={managerSearch}
        onManagerSearch={setManagerSearch}
        managersFound={managerSearchResponse?.data?.data}
        onSubmit={handleSubmit}
        isLoading={createTeamMutation.isLoading || editTeamMutation.isLoading}
        editing={editing}
        onDeleteClick={handleDeleteClick}
        onSetMemberEndDate={async (profileId, endDate) => {
          if (!editing) return;
          try {
            await teamsServices.setMemberEndDate(editing.id, profileId, endDate);
            toast.setMessage('End date set successfully.');
            toast.setSeverity('success');
            toast.showToast();
            onEditTeam?.(editing);
          } catch {
            toast.setMessage('Failed to set end date.');
            toast.setSeverity('error');
            toast.showToast();
          }
        }}
        setLockDrawer={setLockDrawer}
      />
      <Modal
        isCentered
        size="sm"
        modalTitle="Are you sure?"
        bodyRender={<>Are you sure you want to remove it? This action cannot be undone.</>}
        onConfirmLabel="Yes, remove it"
        onConfirmButtonVariant="red"
        onConfirmClick={() => {
          handleDeleteConfirmClick(confirmDelete || '');
        }}
        isButtonDisabled={deleteTeamMutation.isLoading}
        onClose={() => setConfirmDelete(undefined)}
        isOpen={Boolean(confirmDelete)}
      />
    </>
  );
};

export default NewTeamFormModal;
